/**
 * PWA helpers for the phone view (scope §5.11). Owner: web-phone-pwa.
 *
 * The service worker keeps signed-in API reads in a cache named for the current user. The worker implementation is
 * in service-worker.ts; shared-shell wiring selects it as VitePWA's worker source and tells it who signed in. Staff
 * writes are never cached or queued here (no Background Sync). Contractor photos and answers wait in the portal
 * page's IndexedDB queue (#1018), not in this worker.
 */
import { safeStorage } from '@patchbay/ui'
import * as React from 'react'
import { API_CACHE_PREFIX, LEGACY_API_CACHE, type OfflineCacheMessage, offlineCacheName } from './offline-cache-policy'

export {
  API_CACHE_MAX_AGE_SECONDS,
  API_CACHE_MAX_ENTRIES,
  API_CACHE_PREFIX,
  isCacheableApiRequest,
  LEGACY_API_CACHE,
  type OfflineCacheMessage,
  offlineCacheName,
} from './offline-cache-policy'

interface CacheEnvironment {
  storage?: Pick<CacheStorage, 'keys' | 'delete'>
  controller?: Pick<ServiceWorker, 'postMessage'> | null
}

function browserCacheEnvironment(): CacheEnvironment {
  return {
    storage: typeof caches === 'undefined' ? undefined : caches,
    controller: typeof navigator === 'undefined' ? null : navigator.serviceWorker?.controller,
  }
}

async function deleteApiCaches(storage: Pick<CacheStorage, 'keys' | 'delete'> | undefined, keep?: string): Promise<void> {
  if (!storage) return
  const names = await storage.keys()
  await Promise.all(
    names
      .filter((name) => (name === LEGACY_API_CACHE || name.startsWith(API_CACHE_PREFIX)) && name !== keep)
      .map((name) => storage.delete(name)),
  )
}

/** Select one user's cache and remove every prior user's/legacy API cache before offline rows can render. */
export async function setOfflineCacheUser(userId: string, env: CacheEnvironment = browserCacheEnvironment()): Promise<void> {
  const keep = offlineCacheName(userId)
  await deleteApiCaches(env.storage, keep)
  env.controller?.postMessage({ type: 'patchbay:cache-user', userId } satisfies OfflineCacheMessage)
}

/**
 * Tells the service worker who is signed in, on sign-in and whenever a (new) worker takes control of the page, since
 * a worker that was restarted or just installed has no user and caches nothing until it hears one.
 */
export function useOfflineCacheUser(userId: string | null): void {
  React.useEffect(() => {
    if (!userId) return
    const select = () => void setOfflineCacheUser(userId).catch(() => {})
    select()
    const sw = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker
    sw?.addEventListener('controllerchange', select)
    return () => sw?.removeEventListener('controllerchange', select)
  }, [userId])
}

export function useOnline(): boolean {
  const [online, setOnline] = React.useState(() => typeof navigator === 'undefined' || navigator.onLine !== false)
  React.useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])
  return online
}

/**
 * Drop every cached API read. The sign-out flow (slice 14 `web-shell`, `src/features/auth`) must call this so a
 * shared phone never shows the previous person's cases offline.
 */
export async function clearOfflineCaches(
  reason: Extract<OfflineCacheMessage, { type: 'patchbay:cache-clear' }>['reason'] = 'sign-out',
  env: CacheEnvironment = browserCacheEnvironment(),
): Promise<void> {
  try {
    await deleteApiCaches(env.storage)
    env.controller?.postMessage({ type: 'patchbay:cache-clear', reason } satisfies OfflineCacheMessage)
  } catch {
    /* Cache Storage blocked: nothing was cached either */
  }
}

export interface InstallContext {
  userAgent: string
  maxTouchPoints: number
  standalone: boolean
}

/** iOS Safari has no install prompt; it needs "Share, then Add to Home Screen". iPadOS reports as a Mac. */
export function needsIosInstallHint(ctx: InstallContext): boolean {
  if (ctx.standalone) return false
  const ua = ctx.userAgent
  const ios = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && ctx.maxTouchPoints > 1)
  if (!ios) return false
  // Chrome/Firefox/Edge on iOS can't add a web app to the home screen from their own menu reliably; Safari can.
  return !/CriOS|FxiOS|EdgiOS/.test(ua)
}

function currentInstallContext(): InstallContext {
  const nav = navigator as Navigator & { standalone?: boolean }
  const standalone =
    nav.standalone === true || (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches)
  return { userAgent: nav.userAgent, maxTouchPoints: nav.maxTouchPoints ?? 0, standalone }
}

const HINT_KEY = 'patchbay:ios-install-hint-dismissed'

/** Per-viewer convenience only: dismissing the hint is remembered in localStorage when it is available. */
export function useIosInstallHint(): { show: boolean; dismiss: () => void } {
  const [show, setShow] = React.useState(false)
  React.useEffect(() => {
    setShow(safeStorage.getItem(HINT_KEY) !== '1' && needsIosInstallHint(currentInstallContext()))
  }, [])
  return {
    show,
    dismiss: () => {
      safeStorage.setItem(HINT_KEY, '1')
      setShow(false)
    },
  }
}
